// Port of webapp/src/components/cardDetail/commentsList.tsx + comment.tsx

import {ReactElement, useState} from 'react'
import {Board} from '../../types/board'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import {CommentBlock} from '../../store/comments'
import {useUsers} from '../../store/users'
import * as mutator from '../../state/mutator'
import {Utils} from '../../common/utils'
import {Icon} from '../common/icons'
import {InlineTextInput} from './propertyEditors'

type Props = {
    board: Board
    card: Card
    comments: CommentBlock[]
    readOnly: boolean
}

export default function CommentsList({board, card, comments, readOnly}: Props): ReactElement {
    const [draft, setDraft] = useState('')
    const me = useUsers((state) => state.me)
    const users = useUsers((state) => state.users)

    const send = async (): Promise<void> => {
        const text = draft.trim()
        if (!text) {
            return
        }
        setDraft('')
        await mutator.insertComment(board.id, card, text)
    }

    return (
        <div className='CommentsList w-full pt-6'>
            <div className='mb-3 flex items-center gap-2 text-sm font-semibold opacity-80'>
                <Icon name='message' size={14}/>
                评论
                {comments.length > 0 && <span className='font-normal opacity-60'>({comments.length})</span>}
            </div>

            <ol className='m-0 list-none space-y-4 p-0'>
                {comments.map((comment) => {
                    const author: IUser | undefined = users[comment.createdBy]
                    return (
                        <li key={comment.id} className='group flex items-start gap-3'>
                            <Avatar user={author}/>
                            <div className='min-w-0 flex-1'>
                                <div className='flex items-baseline gap-2'>
                                    <span className='text-sm font-semibold'>{author?.username || '未知用户'}</span>
                                    <span className='text-xxs opacity-60'>{Utils.relativeDisplayDateTime(new Date(comment.createAt))}</span>
                                    {!readOnly && comment.createdBy === me?.id && (
                                        <button
                                            type='button'
                                            className='IconButton ml-auto h-6 w-6 opacity-0 group-hover:opacity-100'
                                            aria-label='删除评论'
                                            onClick={() => void mutator.deleteComment(board.id, comment)}
                                        >
                                            <Icon name='trash' size={13}/>
                                        </button>
                                    )}
                                </div>
                                {comment.createdBy === me?.id ? (
                                    <InlineTextInput
                                        multiline={true}
                                        className='text-sm'
                                        value={comment.title}
                                        onCommit={(text) => void mutator.updateComment(board.id, comment, text)}
                                    />
                                ) : (
                                    <p className='m-0 mt-0.5 whitespace-pre-wrap text-sm'>{comment.title}</p>
                                )}
                            </div>
                        </li>
                    )
                })}
            </ol>

            {!readOnly && (
                <div className='mt-4 flex items-start gap-3'>
                    <Avatar user={me || undefined}/>
                    <div className='flex-1'>
                        <textarea
                            className='w-full resize-none rounded border border-main-fg/15 px-2 py-1.5 text-sm outline-none focus:border-button-bg'
                            rows={2}
                            value={draft}
                            placeholder='添加评论……'
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    void send()
                                }
                            }}
                        />
                        <div className='mt-1 flex justify-end'>
                            <button type='button' className='Button Button--primary' onClick={() => void send()} disabled={!draft.trim()}>
                                发送
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function Avatar({user}: {user: IUser | null | undefined}): ReactElement {
    const initials = user ? (user.firstname.charAt(0) + (user.lastname.charAt(0) || '')).toUpperCase() : '?'
    return (
        <span
            className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/10 text-xs font-semibold'
            title={user?.username}
            aria-hidden={true}
        >
            {initials}
        </span>
    )
}
